class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-drawer-close]')) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.dataset.open === 'true') {
        this.close();
      }
    });
  }

  open() {
    this.dataset.open = 'true';
  }

  close() {
    this.dataset.open = 'false';
  }
}

customElements.define('filter-drawer', FilterDrawer);

function getFilterDrawer() {
  return document.querySelector('filter-drawer');
}

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-filter-drawer-toggle]')) {
    event.preventDefault();
    getFilterDrawer()?.open();
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-collection]');
  if (!root) return;

  const sectionId = root.dataset.collectionSectionId;
  const layoutEl = root.querySelector('[data-collection-layout]');

  let abortController;
  let requestId = 0;
  let sentinelObserver;
  let soldOutProducts = [];

  function deferSoldOut(url) {
    const availability = new URL(url, window.location.origin).searchParams.getAll('filter.v.availability');
    return !availability.includes('0') || availability.includes('1');
  }

  function addProducts(grid, products, defer) {
    for (const product of [...products]) {
      if (defer && product.dataset.productAvailable === 'false') {
        product.remove();
        soldOutProducts.push(product);
      } else {
        grid.append(product);
      }
    }
  }

  function finishProducts(grid) {
    grid?.append(...soldOutProducts);
    soldOutProducts = [];
  }

  function prepareGrid(grid, url) {
    soldOutProducts = [];
    if (!grid || !deferSoldOut(url)) return;
    addProducts(grid, grid.children, true);
    if (!grid.closest('[data-collection-grid]')?.querySelector('[data-collection-sentinel]')) {
      finishProducts(grid);
    }
  }

  function setLoading(isLoading) {
    layoutEl?.toggleAttribute('data-loading', isLoading);
  }

  function observeSentinel(sentinel) {
    if (!sentinel) return;
    if (!sentinelObserver) {
      sentinelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !layoutEl?.hasAttribute('data-loading')) {
              const link = entry.target.querySelector('[data-collection-loadmore]');
              if (link) render(link.href, { append: true, pushState: false });
            }
          });
        },
        { rootMargin: '600px' }
      );
    }
    sentinelObserver.observe(sentinel);
  }

  async function render(url, { append = false, pushState = true } = {}) {
    abortController?.abort();
    abortController = new AbortController();
    const thisRequest = ++requestId;

    setLoading(true);

    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    let html;
    try {
      const response = await fetch(fetchUrl.toString(), { signal: abortController.signal });
      html = await response.text();
    } catch (error) {
      setLoading(false);
      return;
    }

    if (thisRequest !== requestId) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('[data-collection-grid]');
    const grid = root.querySelector('[data-collection-grid]');

    if (append) {
      const currentProducts = grid?.querySelector('[data-product-grid]');
      const newProducts = newGrid?.querySelector('[data-product-grid]');
      if (currentProducts && newProducts) {
        addProducts(currentProducts, newProducts.children, deferSoldOut(url));
      }

      const sentinel = grid?.querySelector('[data-collection-sentinel]');
      const newSentinel = newGrid?.querySelector('[data-collection-sentinel]');
      if (sentinel) sentinelObserver?.unobserve(sentinel);
      if (newSentinel) {
        sentinel?.replaceWith(newSentinel);
        observeSentinel(newSentinel);
      } else {
        sentinel?.remove();
        finishProducts(currentProducts);
      }
    } else {
      if (pushState) history.pushState({}, '', url);

      if (grid && newGrid) grid.replaceWith(newGrid);
      prepareGrid(newGrid?.querySelector('[data-product-grid]'), url);

      const toolbar = root.querySelector('[data-collection-toolbar]');
      const newToolbar = doc.querySelector('[data-collection-toolbar]');
      if (toolbar && newToolbar) toolbar.replaceWith(newToolbar);

      const drawerInner = document.querySelector('[data-filter-drawer-inner]');
      const newDrawerInner = doc.querySelector('[data-filter-drawer-inner]');
      if (drawerInner && newDrawerInner) drawerInner.innerHTML = newDrawerInner.innerHTML;

      const newSentinel = root.querySelector('[data-collection-sentinel]');
      observeSentinel(newSentinel);
    }

    setLoading(false);
  }

  function applyFromForm(form) {
    const params = new URLSearchParams(new FormData(form));
    const sortSelect = root.querySelector('[data-collection-sort]');
    if (sortSelect) params.set('sort_by', sortSelect.value);
    render(`${root.dataset.collectionUrl}?${params.toString()}`);
  }

  function applySort(value) {
    const form = document.querySelector('[data-collection-filters]');
    const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
    params.set('sort_by', value);
    render(`${root.dataset.collectionUrl}?${params.toString()}`);
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-collection-sort]')) {
      applySort(event.target.value);
      return;
    }

    const form = event.target.closest('[data-collection-filters]');
    if (form && event.target.matches('input[type="checkbox"]')) {
      applyFromForm(form);
    }
  });

  let priceDebounce;
  document.addEventListener('input', (event) => {
    const form = event.target.closest('[data-collection-filters]');
    if (!form || event.target.type !== 'number') return;

    clearTimeout(priceDebounce);
    priceDebounce = setTimeout(() => applyFromForm(form), 600);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-collection-filters]');
    if (!form) return;
    event.preventDefault();
    applyFromForm(form);
  });

  document.addEventListener('click', (event) => {
    const loadmore = event.target.closest('[data-collection-loadmore]');
    if (loadmore) {
      event.preventDefault();
      render(loadmore.href, { append: true, pushState: false });
      return;
    }

    const link = event.target.closest('[data-collection-link]');
    if (!link) return;

    event.preventDefault();
    render(link.href);

    if (event.target.closest('filter-drawer')) {
      getFilterDrawer()?.close();
    }
  });

  window.addEventListener('popstate', () => {
    render(window.location.href, { pushState: false });
  });

  prepareGrid(root.querySelector('[data-product-grid]'), window.location.href);
  observeSentinel(root.querySelector('[data-collection-sentinel]'));
});
